/* PLP Sort: server-fetched grid + client reorder for non-default sorts.
 *
 * Bestselling uses Shopify's native `manual` order with normal pagination
 * intact — no extra work, page renders pre-sorted.
 *
 * The other four sorts (Newest, Price asc/desc, Lead Time asc) cannot use
 * Shopify's native sort_by because the merchandised values we want to sort
 * by live in metafields that the storefront sort doesn't see:
 *   - "from $X" pricing (custom_combined_collection.from_pricing)
 *   - Lead time minimum (custom_combined_listing_lead_time_minimum)
 *   - Launch-date override (custom.launch_date_override) with published_at fallback
 *
 * For those, we fetch every paginated page of the collection's section in
 * parallel, merge the cards, and order client-side using data-sort-* attrs
 * emitted by main-collection-product-grid.liquid. Pagination collapses to a
 * single view while a non-default sort is active.
 *
 * Persistence: URL only. `?sort=<key>` for non-default sorts; absent param =
 * Bestselling.
 */

(function () {
  const URL_PARAM = 'sort';
  const DEFAULT_SORT = 'bestselling';

  /** Sort config keyed by internal sort key.
   *   - shopifySortBy: used only for the Section Rendering API fetch. We pass
   *     `manual` for every non-default sort because we override the order
   *     client-side anyway; using `manual` keeps the fetch consistent.
   *   - clientReorder: function(items[]) -> items[]. null = use server order. */
  const SORT_CONFIG = {
    bestselling: { shopifySortBy: 'manual', clientReorder: null },
    newest: {
      shopifySortBy: 'manual',
      clientReorder: (items) => byNumericAttr(items, 'sortNewest', 'desc'),
    },
    'price-asc': {
      shopifySortBy: 'manual',
      clientReorder: (items) => byNumericAttr(items, 'sortPrice', 'asc'),
    },
    'price-desc': {
      shopifySortBy: 'manual',
      clientReorder: (items) => byNumericAttr(items, 'sortPrice', 'desc'),
    },
    'lead-time-asc': {
      shopifySortBy: 'manual',
      clientReorder: (items) => byNumericAttr(items, 'sortLeadTime', 'asc'),
    },
  };

  function byNumericAttr(items, attr, direction) {
    const dirMul = direction === 'asc' ? 1 : -1;
    return items.slice().sort((a, b) => {
      const av = Number(a.dataset[attr]);
      const bv = Number(b.dataset[attr]);
      const aMissing = !Number.isFinite(av);
      const bMissing = !Number.isFinite(bv);
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (av === bv) {
        return Number(a.dataset.sortIndex || 0) - Number(b.dataset.sortIndex || 0);
      }
      return (av - bv) * dirMul;
    });
  }

  function isValidSort(value) {
    return Object.prototype.hasOwnProperty.call(SORT_CONFIG, value);
  }

  /** Read the active sort from the URL. `?sort=<key>` is authoritative;
   * absence means Bestselling. */
  function readInitialSort() {
    const params = new URLSearchParams(window.location.search);
    const sortKey = params.get(URL_PARAM);
    if (sortKey && isValidSort(sortKey)) return sortKey;
    return DEFAULT_SORT;
  }

  function getGrid() {
    return document.getElementById('product-grid');
  }

  function getSectionId() {
    const grid = getGrid();
    return grid && grid.dataset.id;
  }

  /** Build the URL we push to the address bar — keeps filter.* and other
   * params, sets `?sort=<key>` (or drops it for the default sort). */
  function buildDisplayUrl(sortKey) {
    const url = new URL(window.location.href);
    // Strip legacy `?sort_by=...` if present (we no longer use native sorts).
    url.searchParams.delete('sort_by');
    url.searchParams.delete(URL_PARAM);
    if (sortKey !== DEFAULT_SORT) {
      url.searchParams.set(URL_PARAM, sortKey);
    }
    const qs = url.searchParams.toString();
    return url.pathname + (qs ? '?' + qs : '') + url.hash;
  }

  /** Build a section_id fetch URL for a given Shopify sort_by value and page. */
  function buildFetchUrl(shopifySortBy, page) {
    const sectionId = getSectionId();
    const url = new URL(window.location.pathname, window.location.origin);
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (key === URL_PARAM || key === 'sort_by' || key === 'section_id' || key === 'page') return;
      url.searchParams.append(key, value);
    });
    url.searchParams.set('section_id', sectionId);
    if (shopifySortBy && shopifySortBy !== 'manual') {
      url.searchParams.set('sort_by', shopifySortBy);
    }
    if (page && page > 1) url.searchParams.set('page', String(page));
    return url.pathname + '?' + url.searchParams.toString();
  }

  function setLoading(on) {
    const grid = document.querySelector('#ProductGridContainer .collection');
    if (grid) grid.classList.toggle('loading', on);
    const spinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    spinners.forEach((s) => s.classList.toggle('hidden', !on));
    const count = document.getElementById('ProductCount');
    if (count) count.classList.toggle('loading', on);
  }

  function parseSection(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function swapGridFromHtml(html) {
    const doc = parseSection(html);
    const newGridContainer = doc.getElementById('ProductGridContainer');
    if (newGridContainer) {
      document.getElementById('ProductGridContainer').innerHTML = newGridContainer.innerHTML;
    }
    const newCount = doc.getElementById('ProductCount');
    if (newCount) {
      const cur = document.getElementById('ProductCount');
      if (cur) cur.innerHTML = newCount.innerHTML;
    }
  }

  /** Fetch every paginated page of the section and return the merged items. */
  async function fetchAllPagesGrid(shopifySortBy) {
    const firstUrl = buildFetchUrl(shopifySortBy, 1);
    const firstHtml = await fetch(firstUrl).then((r) => r.text());
    const firstDoc = parseSection(firstHtml);
    const firstGrid = firstDoc.getElementById('product-grid');
    if (!firstGrid) return { firstDoc, items: [] };

    const items = Array.from(firstGrid.children).filter((el) => el.tagName === 'LI');

    let lastPage = 1;
    firstDoc.querySelectorAll('.pagination__list a, nav.pagination a').forEach((a) => {
      const text = (a.textContent || '').trim();
      const n = parseInt(text, 10);
      if (!Number.isNaN(n) && n > lastPage) lastPage = n;
      const href = a.getAttribute('href') || '';
      const m = href.match(/[?&]page=(\d+)/);
      if (m) {
        const p = parseInt(m[1], 10);
        if (p > lastPage) lastPage = p;
      }
    });

    if (lastPage > 1) {
      const pages = [];
      for (let p = 2; p <= lastPage; p++) pages.push(p);
      const responses = await Promise.all(
        pages.map((p) => fetch(buildFetchUrl(shopifySortBy, p)).then((r) => r.text()))
      );
      responses.forEach((html) => {
        const doc = parseSection(html);
        const grid = doc.getElementById('product-grid');
        if (!grid) return;
        Array.from(grid.children)
          .filter((el) => el.tagName === 'LI')
          .forEach((li) => items.push(li));
      });
    }

    return { firstDoc, items };
  }

  async function applyClientReorderedSort(sortKey) {
    const config = SORT_CONFIG[sortKey];
    const { firstDoc, items } = await fetchAllPagesGrid(config.shopifySortBy);
    if (!firstDoc) return;

    const sorted = config.clientReorder(items);
    const newGridContainer = firstDoc.getElementById('ProductGridContainer');
    if (!newGridContainer) return;

    const newGrid = newGridContainer.querySelector('#product-grid');
    if (newGrid) {
      newGrid.innerHTML = '';
      sorted.forEach((li) => newGrid.appendChild(li));
    }
    // Collapse pagination — we've shown everything on one page.
    newGridContainer.querySelectorAll('.pagination__list, nav.pagination').forEach((el) => el.remove());

    document.getElementById('ProductGridContainer').innerHTML = newGridContainer.innerHTML;
    const newCount = firstDoc.getElementById('ProductCount');
    if (newCount) {
      const cur = document.getElementById('ProductCount');
      if (cur) cur.innerHTML = newCount.innerHTML;
    }
  }

  async function applyBestselling() {
    const url = buildFetchUrl(SORT_CONFIG.bestselling.shopifySortBy, getCurrentPage());
    const html = await fetch(url).then((r) => r.text());
    swapGridFromHtml(html);
  }

  function getCurrentPage() {
    const p = new URLSearchParams(window.location.search).get('page');
    const n = parseInt(p, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  function persist(sortKey) {
    window.history.pushState({ plpSort: sortKey }, '', buildDisplayUrl(sortKey));
  }

  function emitUpdated() {
    document.dispatchEvent(new CustomEvent('filter-applied', { bubbles: true, detail: { source: 'plp-sort' } }));
  }

  /** Publish an analytics event via the Shopify Web Pixels API. Other
   * trackers in the theme use the same `Shopify.analytics.publish` channel
   * (see assets/analytics.js); page_path and page_type are added by the
   * pixel's createGeneralBody helper. */
  function trackAnalyticsEvent(eventName, customData) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, customData || {});
      }
    } catch (e) {
      /* analytics is best-effort */
    }
  }

  /** True if the URL already encodes this sort (so the server-rendered grid
   * is the right starting point and no extra work is needed). Only Bestselling
   * benefits — every other sort requires a multi-page fetch + client reorder. */
  function urlMatchesSort(sortKey) {
    const params = new URLSearchParams(window.location.search);
    if (sortKey === DEFAULT_SORT) {
      return !params.has(URL_PARAM);
    }
    return false;
  }

  class PlpSort extends HTMLElement {
    constructor() {
      super();
      this._currentSort = DEFAULT_SORT;
      this._fetching = false;
    }

    connectedCallback() {
      this.selectEl = this.querySelector('[data-plp-sort-select]');
      this.sheetEl = this.querySelector('[data-plp-sort-sheet]');
      this.mobileTrigger = this.querySelector('[data-plp-sort-mobile-trigger]');
      this.sheetClose = this.querySelector('[data-plp-sort-sheet-close]');
      this.sheetOptions = Array.from(this.querySelectorAll('[data-plp-sort-option]'));

      const initial = readInitialSort();
      this._currentSort = initial;
      this.syncControls(initial);

      // Only re-render if the URL doesn't already encode this sort. When the
      // user lands on ?sort_by=price-ascending, Shopify has already returned
      // pre-sorted HTML, so we skip the redundant fetch.
      if (!urlMatchesSort(initial)) {
        this.applySort(initial, { persist: false });
      }

      this.bindEvents();
    }

    bindEvents() {
      if (this.selectEl) {
        // Fire sort_opened only on the mousedown that targets the <select> itself.
        // With appearance: base-select, mousedown on an <option> inside the
        // picker bubbles up to the <select>, which would double-emit. Gate the
        // listener so it ignores bubbled events from descendant options.
        let openEmittedAt = 0;
        const emitOpened = () => {
          const now = Date.now();
          // Coalesce mousedown + keydown within 250ms of each other.
          if (now - openEmittedAt < 250) return;
          openEmittedAt = now;
          trackAnalyticsEvent('sort_opened', {});
        };
        this.selectEl.addEventListener('mousedown', (event) => {
          if (event.target !== this.selectEl) return;
          emitOpened();
        });
        this.selectEl.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            emitOpened();
          }
        });
        this.selectEl.addEventListener('change', () => {
          this.applySort(this.selectEl.value, { userInitiated: true });
        });
      }

      if (this.mobileTrigger && this.sheetEl) {
        this.mobileTrigger.addEventListener('click', () => {
          trackAnalyticsEvent('sort_opened', {});
          this.openSheet();
        });
      }

      if (this.sheetClose) {
        this.sheetClose.addEventListener('click', () => this.closeSheet());
      }

      if (this.sheetEl) {
        this.sheetEl.addEventListener('click', (event) => {
          if (event.target === this.sheetEl) this.closeSheet();
        });
        this.sheetEl.addEventListener('close', () => {
          document.body.classList.remove('plp-sort-sheet-open');
          if (this.mobileTrigger) this.mobileTrigger.setAttribute('aria-expanded', 'false');
        });
      }

      this.sheetOptions.forEach((option) => {
        option.addEventListener('click', () => {
          this.applySort(option.dataset.plpSortOption, { userInitiated: true });
          this.closeSheet();
        });
      });

      this.bindSwipeDismiss();

      window.addEventListener('popstate', () => {
        const next = readInitialSort();
        if (next !== this._currentSort) {
          this.applySort(next, { persist: false });
        }
      });

      // If facets.js swaps the grid (after a filter change), re-apply the
      // current sort to the freshly-rendered list.
      document.addEventListener('filter-applied', (event) => {
        if (event.detail && event.detail.source === 'plp-sort') return;
        if (this._currentSort && this._currentSort !== DEFAULT_SORT) {
          this.applySort(this._currentSort, { persist: false });
        }
      });
    }

    bindSwipeDismiss() {
      if (!this.sheetEl) return;
      let startY = null;
      let lastY = null;

      this.sheetEl.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length !== 1) return;
          startY = e.touches[0].clientY;
          lastY = startY;
        },
        { passive: true }
      );

      this.sheetEl.addEventListener(
        'touchmove',
        (e) => {
          if (startY === null) return;
          lastY = e.touches[0].clientY;
        },
        { passive: true }
      );

      this.sheetEl.addEventListener('touchend', () => {
        if (startY === null || lastY === null) return;
        if (lastY - startY > 80) this.closeSheet();
        startY = null;
        lastY = null;
      });
    }

    openSheet() {
      if (!this.sheetEl || this.sheetEl.open) return;
      this.sheetEl.showModal();
      document.body.classList.add('plp-sort-sheet-open');
      if (this.mobileTrigger) this.mobileTrigger.setAttribute('aria-expanded', 'true');
    }

    closeSheet() {
      if (!this.sheetEl || !this.sheetEl.open) return;
      this.sheetEl.close();
    }

    syncControls(sortKey) {
      if (this.selectEl && this.selectEl.value !== sortKey) {
        this.selectEl.value = sortKey;
      }
      this.sheetOptions.forEach((option) => {
        option.setAttribute(
          'aria-selected',
          option.dataset.plpSortOption === sortKey ? 'true' : 'false'
        );
      });
    }

    async applySort(value, options) {
      if (this._fetching) return;
      const opts = options || {};
      const next = isValidSort(value) ? value : DEFAULT_SORT;
      this._currentSort = next;
      this.syncControls(next);

      if (opts.persist !== false) persist(next);

      if (opts.userInitiated) {
        trackAnalyticsEvent('sort_option_selected', { sort_value: next });
      }

      this._fetching = true;
      setLoading(true);
      try {
        const config = SORT_CONFIG[next];
        if (config.clientReorder) {
          await applyClientReorderedSort(next);
        } else {
          await applyBestselling();
        }
        emitUpdated();
      } catch (e) {
        console.error('[plp-sort] fetch failed', e);
      } finally {
        setLoading(false);
        this._fetching = false;
      }
    }
  }

  if (!customElements.get('plp-sort')) {
    customElements.define('plp-sort', PlpSort);
  }
})();
